import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import ModalPortal, { MODAL_Z } from '../../Components/CommonComponents/ModalPortal';
import { 
  FaCheck, 
  FaTimes, 
  FaUser, 
  FaCalendarAlt, 
  FaInfoCircle, 
  FaIdCard,
  FaEnvelope,
  FaBriefcase,
  FaClock,
  FaPlus
} from 'react-icons/fa';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import { formatExportDate, formatExportDateTime } from '../../UtilityHookFunctions/fetchAllPages';

const AttendanceRequests = ({ embedded = false }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    staffId: '',
    startDate: '',
    endDate: '',
    leaveType: 'casual',
    reason: '',
    idType: 'aadhaar',
    idNumber: '',
  });

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${END_POINT}/attendanceapprove/pending`, {
        headers: {
          Authorization: getJwtToken(),
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch requests');
      }
      
      const data = await response.json();
      if (data.success) {
        const formattedData = data.data?.map(request => ({
          ...request,
          startDate: new Date(request.startDate),
          endDate: new Date(request.endDate),
          createdAt: new Date(request.createdAt)
        }));
        setRequests(formattedData || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await fetch(`${END_POINT}/attendance`, {
        headers: { Authorization: getJwtToken() },
      });
      const data = await res.json();
      setStaffList(data || []);
      if (data && data.length > 0) {
        setFormData((prev) => ({ ...prev, staffId: prev.staffId || data[0]._id }));
      }
    } catch (err) {
      console.error('Error fetching staff list:', err);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      setUpdating(prev => ({ ...prev, [id]: true }));
      
      const response = await fetch(`${END_POINT}/attendanceapprove/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getJwtToken(),
        },
        body: JSON.stringify({ status })
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update status');
      }
      
      if (data.success) {
        toast.success(`Request ${status} successfully`);
        fetchRequests();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update request status');
    } finally {
      setUpdating(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleOpenModal = () => {
    fetchStaff();
    setShowModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.staffId || !formData.startDate || !formData.endDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${END_POINT}/attendanceapprove/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getJwtToken(),
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Leave request submitted successfully');
        setShowModal(false);
        setFormData({
          staffId: '',
          startDate: '',
          endDate: '',
          leaveType: 'casual',
          reason: '',
          idType: 'Aadhaar',
          idNumber: '',
        });
        fetchRequests();
      } else {
        toast.error(data.error || 'Failed to submit leave request');
      }
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error('Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const content = (
      <div className={embedded ? "" : "min-h-screen"}>
        <div className={embedded ? "w-full" : "w-full px-4 py-8"}>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
              <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                <FaCalendarAlt className="mr-2 text-blue-600" /> Leave Requests
              </h1>
              <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                disabled={!requests.length}
                onClick={() =>
                  exportToExcel(requests, 'Leave_Requests', [
                    { key: 'staffId.name', label: 'Staff Name' },
                    { key: 'staffId.email', label: 'Email' },
                    { key: 'staffId.position', label: 'Position' },
                    { key: 'staffId.department', label: 'Department' },
                    { key: 'leaveType', label: 'Leave Type' },
                    { key: 'startDate', label: 'Start Date', format: formatExportDate },
                    { key: 'endDate', label: 'End Date', format: formatExportDate },
                    { key: 'reason', label: 'Reason' },
                    { key: 'idType', label: 'ID Type', format: (v) => (v ? String(v).toUpperCase() : '') },
                    { key: 'idNumber', label: 'ID Number' },
                    { key: 'status', label: 'Status' },
                    { key: 'createdAt', label: 'Requested On', format: formatExportDateTime },
                  ])
                }
                className="flex items-center justify-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export to Excel
              </button>
              <button
                onClick={handleOpenModal}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition"
              >
                <FaPlus size={14} /> Submit Leave Request
              </button>
              </div>
            </div>
            
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <FaInfoCircle className="mx-auto text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600">No pending leave requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests?.map((request) => (
                  <div key={request._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start w-full">
                      <div className="space-y-3 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex items-center">
                            <FaUser className="text-blue-500 mr-2" />
                            <h3 className="text-lg font-semibold">{request.staffId?.name || 'N/A'}</h3>
                          </div>
                          <div className="flex items-center">
                            <FaEnvelope className="text-gray-400 mr-1" size={14} />
                            <span className="text-sm text-gray-600">{request.staffId?.email || 'N/A'}</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center text-sm text-gray-600 mb-1">
                              <FaBriefcase className="mr-2 text-blue-400" />
                              <span className="font-medium">Position:</span>
                              <span className="ml-1">{request.staffId?.position || 'N/A'}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <FaUser className="mr-2 text-blue-400" />
                              <span className="font-medium">Department:</span>
                              <span className="ml-1">{request.staffId?.department || 'N/A'}</span>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center text-sm text-gray-600 mb-1">
                              <FaCalendarAlt className="mr-2 text-green-400" />
                              <span className="font-medium">Leave Period:</span>
                              <span className="ml-1">
                                {new Date(request.startDate).toLocaleDateString()} - {new Date(request.endDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <FaClock className="mr-2 text-purple-400" />
                              <span className="font-medium">Type:</span>
                              <span className="ml-1 capitalize">{request.leaveType || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="flex items-start">
                            <FaInfoCircle className="text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-700">Reason for Leave:</p>
                              <p className="text-sm text-gray-600 mt-1">{request.reason || 'No reason provided'}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full">
                            <FaIdCard className="mr-2 text-gray-500" size={14} />
                            <span className="font-medium">{request.idType?.toUpperCase() || 'ID'}:</span>
                            <span className="ml-1 font-mono">{request.idNumber || 'N/A'}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            Requested on: {new Date(request.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 md:mt-0 flex flex-col space-y-2 md:space-y-0 md:space-x-2 md:flex-row">
                        <button
                          onClick={() => updateStatus(request._id, 'approved')}
                          disabled={updating[request._id]}
                          className={`px-4 py-2 rounded-md flex items-center ${
                            updating[request._id] 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : 'bg-green-500 hover:bg-green-600'
                          } text-white`}
                        >
                          {updating[request._id] ? (
                            'Processing...'
                          ) : (
                            <>
                              <FaCheck className="mr-1" /> Approve
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => updateStatus(request._id, 'rejected')}
                          disabled={updating[request._id]}
                          className={`px-4 py-2 rounded-md flex items-center ${
                            updating[request._id] 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : 'bg-red-500 hover:bg-red-600'
                          } text-white`}
                        >
                          {updating[request._id] ? (
                            'Processing...'
                          ) : (
                            <>
                              <FaTimes className="mr-1" /> Reject
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Submit Leave Request Modal */}
        {showModal && (
          <ModalPortal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4`}>
            <div role="dialog" aria-modal="true" className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl">
              <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaCalendarAlt className="text-blue-600" /> Submit Leave Request
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 font-bold text-xl"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleAddSubmit}>
                <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Select Staff Member *</label>
                    {staffList.length === 0 ? (
                      <p className="text-xs text-red-500">No staff found. Please add staff in Staff Profile first.</p>
                    ) : (
                      <select
                        value={formData.staffId}
                        onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
                        required
                        className="w-full border rounded-md p-2.5 text-sm bg-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        {staffList.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.name} ({s.position || 'Staff'})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        required
                        className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">End Date *</label>
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        required
                        className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Leave Type *</label>
                    <select
                      value={formData.leaveType}
                      onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                      className="w-full border rounded-md p-2.5 text-sm bg-white focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="casual">Casual Leave</option>
                      <option value="sick">Sick Leave</option>
                      <option value="annual">Annual Leave</option>
                      <option value="maternity">Maternity Leave</option>
                      <option value="paternity">Paternity Leave</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">ID Document Type</label>
                      <select
                        value={formData.idType}
                        onChange={(e) => setFormData({ ...formData, idType: e.target.value })}
                        className="w-full border rounded-md p-2.5 text-sm bg-white focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="aadhaar">Aadhaar Card</option>
                        <option value="pan">PAN Card</option>
                        <option value="passport">Passport</option>
                        <option value="voter_id">Voter ID</option>
                        <option value="driving_license">Driving License</option>
                        <option value="employee_id">Employee ID</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">ID Number</label>
                      <input
                        type="text"
                        placeholder="e.g. 1234 5678 9012"
                        value={formData.idNumber}
                        onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                        className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Leave</label>
                    <textarea
                      rows={3}
                      placeholder="Specify the reason for requesting leave..."
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow-sm transition disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div></ModalPortal>
        )}
      </div>
  );

  return embedded ? content : <RightWrapper>{content}</RightWrapper>;
};

export default AttendanceRequests;